import { HdBodygraphActivation, HdBodygraphConfig } from '../../bodygraph/hd-bodygraph.models';
import {
  HD_LOCKS_KEYS_LOCKS,
  HdLocksKeysLockMeta,
  HdLocksKeysPeriodId,
  HdLocksKeysScope,
  hdLocksKeysKeyGate,
} from './hd-locks-keys-metadata';

/** Bronsleutel van de Locks & Keys "algemeen"-imprint-activaties. */
export const HD_LOCKS_KEYS_IMPRINT_SOURCE_KEY = 'locks-keys-imprint';

/**
 * "Algemeen"-imprint van een periode: een config met ENKEL de 8 keys van die
 * periode als personality-activaties. De locks zelf zijn niet actief — enkel de
 * keys zijn de actieve ster-armen. Voedt het hoofdwiel in de general-scope
 * i.p.v. een lege config, zodat de gekozen periode als chart zichtbaar is.
 */
export function buildHdLocksKeysGeneralImprintConfig(periodId: HdLocksKeysPeriodId): HdBodygraphConfig {
  const activations: HdBodygraphActivation[] = HD_LOCKS_KEYS_LOCKS.map(lock => {
    const gate = hdLocksKeysKeyGate(lock.gate, periodId);
    return {
      id: `${HD_LOCKS_KEYS_IMPRINT_SOURCE_KEY}:${gate}`,
      gate,
      side: 'personality',
      sourceType: 'manual',
      sourceKey: HD_LOCKS_KEYS_IMPRINT_SOURCE_KEY,
    };
  });
  return { schemaVersion: 1, activations };
}

/**
 * Overzichtsrij per lock: de key van de gekozen periode plus — in de
 * chart-scope — de activaties van de geladen chart op die key- en lock-poort.
 * In de algemene scope zijn de activatie-lijsten leeg en telt elke key als
 * "getoond" (de neutrale ster).
 */
export interface HdLocksKeysLockOverviewEntry {
  readonly lock: HdLocksKeysLockMeta;
  /** Key-poort van de geselecteerde periode. */
  readonly keyGate: number;
  /** Chart-activaties op de key-poort (chart-scope; leeg in algemeen). */
  readonly keyActivations: readonly HdBodygraphActivation[];
  /** Chart-activaties op de lock-poort zelf (informatief, chart-scope). */
  readonly lockActivations: readonly HdBodygraphActivation[];
  /** De chart heeft deze key: minstens één activatie op de key-poort. */
  readonly keyActivated: boolean;
}

export interface HdLocksKeysOverview {
  readonly periodId: HdLocksKeysPeriodId;
  readonly scope: HdLocksKeysScope;
  readonly entries: readonly HdLocksKeysLockOverviewEntry[];
  /** Aantal keys van de periode dat door de chart geactiveerd wordt (0–8). */
  readonly activatedKeyCount: number;
}

/**
 * Bouwt het Locks & Keys-overzicht voor een periode. In de `chart`-scope worden
 * de activaties van de geladen chart per key-/lock-poort verzameld zodat de
 * mandala-laag en het infopaneel weten welke sleutels de chart draagt; in de
 * `general`-scope blijft het een neutrale weergave van de volledige ster.
 */
export function buildHdLocksKeysOverview(
  periodId: HdLocksKeysPeriodId,
  scope: HdLocksKeysScope,
  activations: readonly HdBodygraphActivation[],
): HdLocksKeysOverview {
  const byGate = new Map<number, HdBodygraphActivation[]>();
  if (scope === 'chart') {
    for (const activation of activations) {
      const list = byGate.get(activation.gate);
      if (list == null) {
        byGate.set(activation.gate, [activation]);
      } else {
        list.push(activation);
      }
    }
  }

  const entries = HD_LOCKS_KEYS_LOCKS.map<HdLocksKeysLockOverviewEntry>(lock => {
    const keyGate = hdLocksKeysKeyGate(lock.gate, periodId);
    const keyActivations = byGate.get(keyGate) ?? [];
    return {
      lock,
      keyGate,
      keyActivations,
      lockActivations: byGate.get(lock.gate) ?? [],
      keyActivated: keyActivations.length > 0,
    };
  });

  return {
    periodId,
    scope,
    entries,
    activatedKeyCount: entries.filter(entry => entry.keyActivated).length,
  };
}

/**
 * Per-lock beeld van een relatie: welke van beide personen de sleutel (key-poort)
 * en/of het slot (lock-poort) van die periode draagt. Een lock verschijnt enkel
 * wanneer er een cross-persoon opening is (de sleutel van de ene in het slot van
 * de andere). Zo blijven "dubbels" zichtbaar: iemand die zowel het slot als de
 * sleutel draagt, krijgt beide vlaggen — een zuivere eigen slot-én-sleutel
 * (zonder tegenhanger bij de ander) telt niet als relatie.
 */
export interface HdLocksKeysRelationshipLock {
  readonly lockGate: number;
  readonly keyGate: number;
  readonly person1HasKey: boolean;
  readonly person1HasLock: boolean;
  readonly person2HasKey: boolean;
  readonly person2HasLock: boolean;
}

export function buildHdLocksKeysRelationshipLocks(
  periodId: HdLocksKeysPeriodId,
  person1Gates: Iterable<number>,
  person2Gates: Iterable<number>,
): readonly HdLocksKeysRelationshipLock[] {
  const p1 = new Set(person1Gates);
  const p2 = new Set(person2Gates);
  const result: HdLocksKeysRelationshipLock[] = [];
  for (const lock of HD_LOCKS_KEYS_LOCKS) {
    const keyGate = hdLocksKeysKeyGate(lock.gate, periodId);
    const person1HasKey = p1.has(keyGate);
    const person1HasLock = p1.has(lock.gate);
    const person2HasKey = p2.has(keyGate);
    const person2HasLock = p2.has(lock.gate);
    if ((person1HasKey && person2HasLock) || (person2HasKey && person1HasLock)) {
      result.push({ lockGate: lock.gate, keyGate, person1HasKey, person1HasLock, person2HasKey, person2HasLock });
    }
  }
  return result;
}
